import React from 'react';
import { CcpaSectionName, ScanResult } from '../utils/types';
import { T } from '../designTokens';

const CCPA_STAGES: CcpaSectionName[] = ['uiCompliance', 'trackerDetection', 'gpcValidation', 'privacyPolicy'];

interface PartialScanBannerProps {
  result: ScanResult;
}

export default function PartialScanBanner({ result }: PartialScanBannerProps) {
  // CCPA stages only. The CIPA section (#1392) is derived from tracker
  // detection, so a tracker failure errors both; counting both would report
  // one failed stage as two.
  const sections = CCPA_STAGES.map((k) => result.sections[k]).filter((s) => s !== undefined);
  const failedStagesCount = sections.filter((s) => s.status === 'error').length;
  const hasErrors = result.errors && result.errors.length > 0;

  if (failedStagesCount === 0 && !hasErrors) {
    return null;
  }

  const isTimeout = result.errors && result.errors.some((e) => e.toLowerCase().includes('timeout') || e.toLowerCase().includes('duration'));

  let message = '';
  if (result.status === 'failed') {
    // The scanner's catch-all (failScan) never scores the result, so a
    // failed scan has no completed checks for a score to reflect.
    message = 'Scan failed — the scan could not finish, so this report has no score.';
  } else if (failedStagesCount > 0) {
    message = `Partial scan — ${failedStagesCount} stage(s) failed; score reflects completed checks only.`;
  } else if (isTimeout) {
    message = 'Scan completed partially due to maximum execution limit. Some pages or resources could not be loaded.';
  } else {
    message = 'Partial scan — scan encountered errors but completed. Score reflects completed checks only.';
  }

  // Inline token styles, not utility classes: the dashboard has no Tailwind,
  // and an SVG sized only by `w-5 h-5` fills its container's full width.
  return (
    <div
      role="alert"
      id="partial-scan-banner"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        backgroundColor: T.errorBg,
        border: `1px solid ${T.errorBorder}`,
        borderRadius: '8px',
        fontSize: '14px',
        color: T.red700,
      }}
    >
      <svg
        width="20"
        height="20"
        style={{ flexShrink: 0, color: T.error }}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
      <div style={{ fontWeight: 500 }}>{message}</div>
    </div>
  );
}
